import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, useHistory, useLocation, useParams } from 'react-router-dom';
import { Button, Dimmer, Form, Grid, Loader } from 'semantic-ui-react';
import styled from 'styled-components';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { iRootState } from '../../store';
import SectionLabel from '../../components/SectionLabel';
import { FieldLabel } from '../../styles';
import { maskZipCode } from '../../services/masks';
import { AddressService } from '../../services/address';
import {
  createEstateUnit,
  getEstateUnit,
  updateEstateUnit
} from '../../services/estate-unit';
import {
  CreateEstateUnitPayload,
  UpdateEstateUnitPayload
} from '../../types/EstateUnit';
import {
  dangerNotification,
  successNotification
} from '../../services/notification';
import { UserRolesEnum } from '../../enums/user-roles.enum';
import { isBackOfficeUser } from '../../utils/isBackOfficeUser';

const FormWrapper = styled.div`
  padding: 1rem 1rem 2rem;
`;

const mapState = (state: iRootState) => ({
  user: state.user,
  availableBrokersAndEstates: state.availableBrokersAndEstates
});

type Props = ReturnType<typeof mapState> & RouteComponentProps;

type EstateUnitFormValues = {
  estateId: string;
  name: string;
  zipcode: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  observations: string;
};

const initialValues: EstateUnitFormValues = {
  estateId: '',
  name: '',
  zipcode: '',
  street: '',
  number: '',
  complement: '',
  district: '',
  city: '',
  state: '',
  observations: ''
};

const validationSchema = Yup.object().shape({
  estateId: Yup.string().required('Campo obrigatório'),
  name: Yup.string().required('Campo obrigatório'),
  zipcode: Yup.string().required('Campo obrigatório'),
  street: Yup.string().required('Campo obrigatório'),
  number: Yup.string().required('Campo obrigatório'),
  district: Yup.string().required('Campo obrigatório'),
  city: Yup.string().required('Campo obrigatório'),
  state: Yup.string().max(2, 'UF inválida').required('Campo obrigatório')
});

const EstateUnitFormPage: React.FC<Props> = ({
  user,
  availableBrokersAndEstates,
  history
}) => {
  const { id } = useParams<{ id?: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryEstateId = queryParams.get('estateId') || '';

  const isEdit = Boolean(id);
  const [formValues, setFormValues] = useState<EstateUnitFormValues>(initialValues);
  const [isLoading, setIsLoading] = useState(false);
  const [isCepLoading, setIsCepLoading] = useState(false);
  const [isReady, setIsReady] = useState(!isEdit);

  const isBackOffice = isBackOfficeUser();
  const estateOptions = useMemo(
    () =>
      (availableBrokersAndEstates.estates || []).map(estate => ({
        key: estate.key,
        text: estate.text,
        value: String(estate.value?.id || estate.value)
      })),
    [availableBrokersAndEstates.estates]
  );

  const defaultEstateId = useMemo(() => {
    if (queryEstateId) {
      return queryEstateId;
    }

    if (user.estate?.id) {
      return String(user.estate.id);
    }

    if (availableBrokersAndEstates.activeEstateId) {
      return String(availableBrokersAndEstates.activeEstateId);
    }

    if (estateOptions.length === 1) {
      return estateOptions[0].value;
    }

    return '';
  }, [
    queryEstateId,
    user.estate?.id,
    availableBrokersAndEstates.activeEstateId,
    estateOptions
  ]);

  useEffect(() => {
    if (!isEdit || !id) {
      setFormValues({
        ...initialValues,
        estateId: defaultEstateId
      });
      setIsReady(true);
      return;
    }

    const loadUnit = async () => {
      setIsLoading(true);
      try {
        const unit = await getEstateUnit(id);
        setFormValues({
          estateId: unit.estateId,
          name: unit.name,
          zipcode: unit.zipcode,
          street: unit.street,
          number: unit.number,
          complement: unit.complement || '',
          district: unit.district,
          city: unit.city,
          state: unit.state,
          observations: unit.observations || ''
        });
      } catch {
        dangerNotification('Oops...', 'Não foi possível carregar a unidade.');
        history.push('/admin/estate-units');
      } finally {
        setIsLoading(false);
        setIsReady(true);
      }
    };

    loadUnit();
  }, [defaultEstateId, history, id, isEdit]);

  const changeZipCode = (
    cep: string,
    setFieldValue: (field: string, value: string) => void
  ) => {
    const maskedCep = maskZipCode(cep);
    setFieldValue('zipcode', maskedCep);

    if (maskedCep.length === 9) {
      setIsCepLoading(true);
      AddressService.getAddressByZipCode(maskedCep, user.id)
        .then((address: {
          city: string;
          district: string;
          state: string;
          street: string;
        }) => {
          setFieldValue('city', address.city);
          setFieldValue('district', address.district);
          setFieldValue('state', address.state);
          setFieldValue('street', address.street);
        })
        .catch(error => {
          console.error('address error:', error);
        })
        .finally(() => {
          setIsCepLoading(false);
        });
    }
  };

  const handleSubmit = async (values: EstateUnitFormValues) => {
    setIsLoading(true);

    try {
      if (isEdit && id) {
        const payload: UpdateEstateUnitPayload = {
          name: values.name,
          zipcode: values.zipcode,
          street: values.street,
          number: values.number,
          complement: values.complement || undefined,
          district: values.district,
          city: values.city,
          state: values.state,
          observations: values.observations || undefined
        };
        await updateEstateUnit(id, payload);
        successNotification('Sucesso', 'Unidade atualizada com sucesso.');
      } else {
        const payload: CreateEstateUnitPayload = {
          estateId: values.estateId,
          name: values.name,
          zipcode: values.zipcode,
          street: values.street,
          number: values.number,
          complement: values.complement || undefined,
          district: values.district,
          city: values.city,
          state: values.state,
          observations: values.observations || undefined
        };
        await createEstateUnit(payload);
        successNotification('Sucesso', 'Unidade criada com sucesso.');
      }

      history.push(`/admin/estate-units?estateId=${values.estateId}`);
    } catch (error) {
      dangerNotification(
        'Oops...',
        (error as { message?: string })?.message ||
          'Não foi possível salvar a unidade.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const canSelectEstate =
    isBackOffice &&
    (user.role === UserRolesEnum.brokerAdmin ||
      user.role === UserRolesEnum.brokerAnalyst);

  return (
    <FormWrapper>
      <SectionLabel text={isEdit ? 'EDITAR UNIDADE' : 'NOVA UNIDADE'} />

      <Dimmer active={isLoading || isCepLoading} inverted>
        <Loader />
      </Dimmer>

      {isReady && (
        <Formik
          initialValues={formValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue, handleSubmit: submitForm }) => (
            <Form onSubmit={submitForm}>
              <Grid>
                {canSelectEstate && (
                  <Grid.Row columns={2}>
                    <Grid.Column>
                      <FieldLabel>Imobiliária*</FieldLabel>
                      <Form.Select
                        search
                        options={estateOptions}
                        value={values.estateId}
                        disabled={isEdit}
                        onChange={(_, { value }) =>
                          setFieldValue('estateId', String(value || ''))
                        }
                        error={Boolean(touched.estateId && errors.estateId)}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}

                <Grid.Row columns={2}>
                  <Grid.Column>
                    <FieldLabel>Nome da Unidade*</FieldLabel>
                    <Form.Input
                      value={values.name}
                      onChange={(_, { value }) => setFieldValue('name', String(value))}
                      error={Boolean(touched.name && errors.name)}
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <FieldLabel>CEP*</FieldLabel>
                    <Form.Input
                      value={values.zipcode}
                      onChange={(_, { value }) =>
                        changeZipCode(String(value), setFieldValue)
                      }
                      error={Boolean(touched.zipcode && errors.zipcode)}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row columns={3}>
                  <Grid.Column width={8}>
                    <FieldLabel>Logradouro*</FieldLabel>
                    <Form.Input
                      value={values.street}
                      onChange={(_, { value }) => setFieldValue('street', String(value))}
                      error={Boolean(touched.street && errors.street)}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <FieldLabel>Número*</FieldLabel>
                    <Form.Input
                      value={values.number}
                      onChange={(_, { value }) => setFieldValue('number', String(value))}
                      error={Boolean(touched.number && errors.number)}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <FieldLabel>Complemento</FieldLabel>
                    <Form.Input
                      value={values.complement}
                      onChange={(_, { value }) =>
                        setFieldValue('complement', String(value))
                      }
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row columns={3}>
                  <Grid.Column>
                    <FieldLabel>Bairro*</FieldLabel>
                    <Form.Input
                      value={values.district}
                      onChange={(_, { value }) =>
                        setFieldValue('district', String(value))
                      }
                      error={Boolean(touched.district && errors.district)}
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <FieldLabel>Cidade*</FieldLabel>
                    <Form.Input
                      value={values.city}
                      onChange={(_, { value }) => setFieldValue('city', String(value))}
                      error={Boolean(touched.city && errors.city)}
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <FieldLabel>UF*</FieldLabel>
                    <Form.Input
                      maxLength={2}
                      value={values.state}
                      onChange={(_, { value }) => setFieldValue('state', String(value))}
                      error={Boolean(touched.state && errors.state)}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column>
                    <FieldLabel>Observações</FieldLabel>
                    <Form.TextArea
                      value={values.observations}
                      onChange={(_, { value }) =>
                        setFieldValue('observations', String(value))
                      }
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>

              <Button type="submit" color="green" style={{ marginTop: '1rem' }}>
                Salvar
              </Button>
              <Button
                type="button"
                onClick={() =>
                  history.push(
                    `/admin/estate-units?estateId=${values.estateId || defaultEstateId}`
                  )
                }
              >
                Cancelar
              </Button>
            </Form>
          )}
        </Formik>
      )}
    </FormWrapper>
  );
};

export default connect(mapState)(EstateUnitFormPage);
